/**
 * Capital + Standing (fixed) + Running cost assumption tables.
 * Capital/Standing persist via zeecore fleet-cost ObjectStore.
 * Running rates persist via profitability /v2/config (same store as Costs below).
 * Quantity is read-only from Assets. Editing uses Edit All / Save / Cancel.
 */

import { Fragment, useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { Check, CircleHelp, Save, Settings, Download, Plus, Trash2 } from 'lucide-react';
import { getCurrentUserEmail, readSessionUserEmail } from '../../auth/cognitoAuth';
import { isSessionAdmin } from '../../utils/pageAccess';
import { useUseCase } from '../../contexts/UseCaseContext';
import {
  fetchFleetCostAssumptionsConfig,
  fetchFleetCostAssumptionsDefaults,
  saveFleetCostAssumptionsConfig,
} from '../../services/fleetCostAssumptionsClient';
import {
  fetchTruthProfitabilityConfig,
  fetchTruthProfitabilityConfigDefaults,
  saveTruthProfitabilityConfig,
} from '../../services/profitabilityTruthClient';
import type {
  FleetCostAcquisitionMethod,
  FleetCostAssumptionsConfig,
  FleetCostRentalPeriod,
  FleetCostVehicleType,
  TrailerStandingAssumptions,
  TrailerTypeRow,
} from '../../types/fleetCostAssumptions';
import {
  PROFITABILITY_TONNAGE_CLASSES,
  type ProfitabilityAssumptionsConfig,
  type ProfitabilityDriverCostRate,
  type ProfitabilityTonnageClass,
} from '../../types/profitabilityTruth';
import {
  countFleetCostAcquisitionMixByName,
  countFleetCostQtyByName,
  parseNonNegativeNumber,
  type AssetClassLabel,
} from '../../utils/fleetCostAssumptionsQty';
import { fetchFleetCostAssetClassLabels } from '../../utils/fleetCostAssetLabels';
import {
  isFleetCostDraftDirty,
  isProfitabilityRatesDraftDirty,
} from '../../utils/fleetCostAssumptionsDirty';
import {
  deriveStandingPerDayMaps,
  fleetStandingAnnualGbp,
  FLEET_STANDING_OP_DEFAULTS,
} from '../../utils/fleetCostStandingPerDay';
import {
  ACQUISITION_METHOD_LABELS,
  acquisitionMethodsInUse,
  acquisitionMixCounts,
  RENTAL_PERIOD_LABELS,
  rentalPeriodOf,
  vehicleCapitalAnnualisedGbp,
  withAcquisitionMixFromAssets,
} from '../../utils/fleetCapitalAnnualised';
import { formatFleetMoneyK } from '../../utils/fleetCostFormatMoney';
import {
  countTrailerAcquisitionByDeck,
  countTrailerAssets,
  countTrailerDecks,
  deckCountsApply,
  DEFAULT_TRAILER_STANDING_GBP_PER_YEAR,
  defaultTrailerTypes,
  trailerStandingAllocatedAnnualPerVehicleGbp,
  trailerStandingPoolAnnualGbp,
  withDeckQuantities,
  withTrailerAcquisitionMix,
  resolveTrailerStandingQtys,
  trailerStandingFromConfig,
} from '../../utils/fleetCostTrailerStanding';
import {
  fleetCostConfirmKeyRunning,
  fleetCostConfirmKeyTrailer,
  fleetCostConfirmKeyVehicle,
  fleetCostValueInputClass,
  fleetCostValueTextClass,
  fleetCostValueTitle,
  isFleetCostValueOverridden,
  mergeTabConfirmations,
  resolveFleetCostValueStatus,
  type FleetCostRateConfirmations,
  type FleetCostValueStatus,
} from '../../utils/fleetCostValueOverride';
import { displayTonnageClass } from '../../utils/displayTonnageClass';
import { exportFleetCostAssumptionsCsv } from '../../utils/fleetCostAssumptionsExportCsv';
import { gbpPerMileFromMpgAndPpl } from '../../utils/fuelCostFromMpgPpl';
import { fetchDeclaredFuelPrices } from '../../utils/fuelPriceHistoryLoad';
import ConfigMetaBadge from '../config/ConfigMetaBadge';
import { ConfigChangeHistoryButton } from '../config/ConfigChangeHistoryPanel';
import { AllInSummaryTip, allInAnnualDisplayed, CapitalTotalFormulaTip } from './FleetCostAllInTips';
import { UNKNOWN_CELL } from '../../utils/uiText';

type CostTab = 'capital' | 'operational' | 'running';

const CAPITAL_FIELDS = [
  'unitCost',
  'initialTaxesFees',
  'serviceLife',
  'residualValue',
  'leaseMonthly',
  'leaseTermMonths',
  'leaseUpfront',
  'leaseEndFees',
  'rentalRate',
] as const satisfies ReadonlyArray<keyof FleetCostVehicleType>;

type CapitalNumberField = (typeof CAPITAL_FIELDS)[number];

interface CapitalFieldRow {
  label: string;
  field: CapitalNumberField;
  defaultVal: (v: FleetCostVehicleType) => number;
  display: (n: number) => string;
  step: number;
}

/** Capital field rows per acquisition method; a class only fills its own method's rows. */
const CAPITAL_ROW_GROUPS: ReadonlyArray<{ method: FleetCostAcquisitionMethod; rows: CapitalFieldRow[] }> = [
  {
    method: 'purchase',
    rows: [
      { label: 'Purchase Price (£)', field: 'unitCost', defaultVal: (v) => v.unitCost, display: formatFleetMoneyK, step: 1000 },
      { label: 'Initial Taxes/Fees (£)', field: 'initialTaxesFees', defaultVal: () => 2000, display: formatFleetMoneyK, step: 100 },
      { label: 'Useful Life (yr)', field: 'serviceLife', defaultVal: (v) => v.serviceLife, display: (n) => String(n), step: 1 },
      { label: 'Residual Value (%)', field: 'residualValue', defaultVal: () => 15, display: (n) => `${n}%`, step: 1 },
    ],
  },
  {
    method: 'lease',
    rows: [
      { label: 'Lease (£/month)', field: 'leaseMonthly', defaultVal: () => 0, display: formatFleetMoneyK, step: 10 },
      { label: 'Lease Term (months)', field: 'leaseTermMonths', defaultVal: () => 12, display: (n) => String(n), step: 1 },
      { label: 'Initial Rental / Deposit (£)', field: 'leaseUpfront', defaultVal: () => 0, display: formatFleetMoneyK, step: 100 },
      { label: 'End-of-Term Fees (£)', field: 'leaseEndFees', defaultVal: () => 0, display: formatFleetMoneyK, step: 100 },
    ],
  },
  {
    method: 'rental',
    rows: [
      { label: 'Rental Rate (£)', field: 'rentalRate', defaultVal: () => 0, display: formatFleetMoneyK, step: 10 },
    ],
  },
];

const OPERATIONAL_FIELDS = [
  'insurance',
  'roadTax',
  'oLicense',
  'telematics',
  'cleaning',
  'parking',
  'permits',
  'scheduledMaintenance',
  'vehicleCompliance',
  'overhead',
] as const satisfies ReadonlyArray<keyof FleetCostVehicleType>;

const UNUSED_METHOD_TITLE = 'No vehicles in this class use this method yet. A rate here adds no cost until some do.';

const CAPITAL_FIELD_SET = new Set<string>(CAPITAL_FIELDS);
const OPERATIONAL_FIELD_SET = new Set<string>(OPERATIONAL_FIELDS);

function cloneConfig(config: FleetCostAssumptionsConfig): FleetCostAssumptionsConfig {
  return JSON.parse(JSON.stringify(config)) as FleetCostAssumptionsConfig;
}

function cloneProfitConfig(config: ProfitabilityAssumptionsConfig): ProfitabilityAssumptionsConfig {
  return JSON.parse(JSON.stringify(config)) as ProfitabilityAssumptionsConfig;
}

/** Notify sibling Costs panel that profitability assumptions were rewritten. */
function notifyProfitabilityConfigUpdated() {
  window.dispatchEvent(new CustomEvent('zecure:profitability-config-updated'));
}

/**
 * Hover tip next to a Running-tab field name.
 * Portaled + fixed so the sticky Field column / overflow-x table cannot clip it.
 */
function RunningFieldInfoTip({ label, children }: { label: string; children: ReactNode }) {
  const iconRef = useRef<HTMLSpanElement>(null);
  const [open, setOpen] = useState(false);
  const [pos, setPos] = useState<{ left: number; top: number; placeBelow: boolean } | null>(null);

  const show = () => {
    const el = iconRef.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    const tipWidth = 256;
    const tipHeight = 120;
    const pad = 8;
    let left = rect.right + pad;
    if (left + tipWidth > window.innerWidth - pad) {
      left = Math.max(pad, rect.left - tipWidth - pad);
    }
    const placeBelow = rect.top - tipHeight < pad;
    const top = placeBelow ? rect.bottom + pad : rect.top - pad;
    setPos({ left, top, placeBelow });
    setOpen(true);
  };

  const hide = () => {
    setOpen(false);
    setPos(null);
  };

  return (
    <span
      ref={iconRef}
      className="inline-flex align-middle shrink-0"
      onMouseEnter={show}
      onMouseLeave={hide}
      onFocus={show}
      onBlur={hide}
    >
      <CircleHelp
        className={`w-3.5 h-3.5 cursor-help transition-colors ${
          open ? 'text-success' : 'text-muted-foreground'
        }`}
        aria-hidden
      />
      <span className="sr-only">{label}</span>
      {open && pos && createPortal(
        <div
          role="tooltip"
          className="fixed z-float w-64 rounded border border-accent bg-popover p-3 text-left text-xs font-normal normal-case tracking-normal text-foreground shadow-xl pointer-events-none"
          style={{
            left: pos.left,
            top: pos.top,
            transform: pos.placeBelow ? 'none' : 'translateY(-100%)',
          }}
        >
          {children}
        </div>,
        document.body,
      )}
    </span>
  );
}

function fuelMpgFieldInfo(
  latestFuelPpl: number | null,
  latestFuelPplWeek: string | null,
): ReactNode {
  return (
    <>
      <p className="font-semibold text-success mb-1.5">Fuel (MPG)</p>
      <p className="text-foreground leading-relaxed">
        Optional helper. Enter miles per gallon; we update Fuel £/mi from the latest uploaded
        weekly diesel price. Profit calculations do not read MPG directly.
      </p>
      {latestFuelPpl != null ? (
        <p className="text-foreground leading-relaxed mt-2 pt-2 border-t border-border">
          Latest diesel:{' '}
          <span className="text-warning tabular-nums font-medium">
            {latestFuelPpl.toFixed(2)} p/L
          </span>
          {latestFuelPplWeek ? (
            <span className="text-muted-foreground"> (week {latestFuelPplWeek})</span>
          ) : null}
        </p>
      ) : (
        <p className="text-warning/90 leading-relaxed mt-2 pt-2 border-t border-border">
          No weekly diesel price yet — upload one on Fuel before MPG can set £/mi.
        </p>
      )}
    </>
  );
}

const FUEL_GBP_PER_MI_FIELD_INFO: ReactNode = (
  <>
    <p className="font-semibold text-success mb-1.5">Fuel (£/mi)</p>
    <p className="text-foreground leading-relaxed">
      This is the rate used for profitability fuel cost (miles × £/mi). Type it here, or let
      Fuel (MPG) fill it from the weekly diesel price.
    </p>
    <p className="text-foreground leading-relaxed mt-2 pt-2 border-t border-border font-mono text-xs">
      £/mi = (PPL ÷ 100) × (4.54609 ÷ MPG)
    </p>
    <p className="text-muted-foreground leading-relaxed mt-1">
      PPL = diesel pence per litre; 4.54609 = litres per gallon; MPG = miles per gallon.
    </p>
  </>
);

/** No client vehicle is 26T: its column shows only when Assets has one. */
const HIDDEN_WHEN_UNUSED_CLASS = '26T';

export default function FleetCostAssumptionsTables() {
  const { useCase } = useUseCase();
  const isEmergency = useCase === 'emergency';

  const [configData, setConfigData] = useState<FleetCostAssumptionsConfig | null>(null);
  const [configDraft, setConfigDraft] = useState<FleetCostAssumptionsConfig | null>(null);
  const [fleetDefaults, setFleetDefaults] = useState<FleetCostAssumptionsConfig | null>(null);
  const [profitData, setProfitData] = useState<ProfitabilityAssumptionsConfig | null>(null);
  const [profitDraft, setProfitDraft] = useState<ProfitabilityAssumptionsConfig | null>(null);
  const [profitDefaults, setProfitDefaults] = useState<ProfitabilityAssumptionsConfig | null>(null);
  const [configLoading, setConfigLoading] = useState(false);
  const [configSaving, setConfigSaving] = useState(false);
  const [configError, setConfigError] = useState<string | null>(null);
  const [configWarning, setConfigWarning] = useState<string | null>(null);
  const [standingSyncPending, setStandingSyncPending] = useState(false);
  const [editMode, setEditMode] = useState(false);
  /** Set by Reset; cleared on any manual edit / Revert / Cancel. Save then stamps system defaults. */
  const [restoreDefaultsPending, setRestoreDefaultsPending] = useState(false);
  const [activeTab, setActiveTab] = useState<CostTab>('capital');
  const [assetClassLabels, setAssetClassLabels] = useState<AssetClassLabel[] | null>(null);
  /** Assets could not be read (null labels alone also means "still loading"). */
  const [assetLabelsFailed, setAssetLabelsFailed] = useState(false);
  const [assetQtyLoading, setAssetQtyLoading] = useState(true);

  const fetchConfig = useCallback(async () => {
    setConfigLoading(true);
    setConfigError(null);
    try {
      const [fleet, profit, fleetDef, profitDef] = await Promise.all([
        fetchFleetCostAssumptionsConfig(),
        fetchTruthProfitabilityConfig().catch(() => null),
        fetchFleetCostAssumptionsDefaults().catch(() => null),
        fetchTruthProfitabilityConfigDefaults().catch(() => null),
      ]);
      setConfigData(fleet);
      setConfigDraft(cloneConfig(fleet));
      setFleetDefaults(fleetDef);
      setProfitData(profit);
      setProfitDraft(profit ? cloneProfitConfig(profit) : null);
      setProfitDefaults(profitDef);
    } catch (err) {
      setConfigError(err instanceof Error ? err.message : 'Failed to load fleet cost assumptions');
    } finally {
      setConfigLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!configData && !configLoading) {
      void fetchConfig();
    }
  }, [configData, configLoading, fetchConfig]);

  useEffect(() => {
    let cancelled = false;
    setAssetQtyLoading(true);
    void fetchFleetCostAssetClassLabels()
      .then((labels) => {
        if (cancelled) return;
        setAssetClassLabels(labels);
        setAssetLabelsFailed(false);
      })
      .catch(() => {
        if (cancelled) return;
        setAssetClassLabels(null);
        setAssetLabelsFailed(true);
      })
      .finally(() => {
        if (!cancelled) setAssetQtyLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const editModeRef = useRef(editMode);
  useEffect(() => {
    editModeRef.current = editMode;
  }, [editMode]);

  useEffect(() => {
    const onUpdated = () => {
      if (editModeRef.current) return;
      void fetchTruthProfitabilityConfig()
        .then((profit) => {
          if (!profit) return;
          // Preserve confirm stamps if a sibling refresh returns an older payload
          // that omitted rate_confirmations (e.g. standing £/day sync).
          setProfitData((prev) => {
            const incoming = profit.rate_confirmations;
            const keep =
              incoming && Object.keys(incoming).length > 0
                ? incoming
                : prev?.rate_confirmations;
            const next: ProfitabilityAssumptionsConfig = keep
              ? { ...profit, rate_confirmations: keep }
              : profit;
            return next;
          });
          setProfitDraft((prev) => {
            const incoming = profit.rate_confirmations;
            const keep =
              incoming && Object.keys(incoming).length > 0
                ? incoming
                : prev?.rate_confirmations;
            const next: ProfitabilityAssumptionsConfig = keep
              ? { ...profit, rate_confirmations: keep }
              : profit;
            return cloneProfitConfig(next);
          });
        })
        .catch(() => {
          /* keep current running rates */
        });
    };
    window.addEventListener('zecure:profitability-config-updated', onUpdated);
    return () => window.removeEventListener('zecure:profitability-config-updated', onUpdated);
  }, []);

  const active = editMode ? configDraft : configData;
  const activeProfit = editMode ? profitDraft : profitData;
  /** Vehicle Cost Rates always apply by tonnage class. */
  const isRunningTab = activeTab === 'running';

  /** Latest confirmed Keyfuels diesel PPL (pence) — used to derive Fuel £/mi from MPG. */
  const [latestFuelPpl, setLatestFuelPpl] = useState<number | null>(null);
  const [latestFuelPplWeek, setLatestFuelPplWeek] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      try {
        const { entries } = await fetchDeclaredFuelPrices();
        if (cancelled) return;
        const confirmed = entries.find((e) => e.source !== 'inferred');
        if (confirmed && Number.isFinite(confirmed.pencePerLitre) && confirmed.pencePerLitre > 0) {
          setLatestFuelPpl(confirmed.pencePerLitre);
          setLatestFuelPplWeek(confirmed.weekOf);
        } else {
          setLatestFuelPpl(null);
          setLatestFuelPplWeek(null);
        }
      } catch {
        if (!cancelled) {
          setLatestFuelPpl(null);
          setLatestFuelPplWeek(null);
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  // When weekly PPL arrives after MPG was entered, re-derive Fuel £/mi for classes with MPG.
  useEffect(() => {
    if (latestFuelPpl == null || !editMode) return;
    setProfitDraft((prev) => {
      if (!prev?.fuel_mpg_tonnage) return prev;
      let changed = false;
      const nextFuel = {
        ...(prev.fuel_cost_tonnage ?? ({} as Record<ProfitabilityTonnageClass, number>)),
      };
      for (const [key, mpg] of Object.entries(prev.fuel_mpg_tonnage)) {
        if (typeof mpg !== 'number' || !(mpg > 0)) continue;
        const derived = gbpPerMileFromMpgAndPpl(mpg, latestFuelPpl);
        if (derived == null) continue;
        const tonnageKey = key as ProfitabilityTonnageClass;
        if (nextFuel[tonnageKey] !== derived) {
          nextFuel[tonnageKey] = derived;
          changed = true;
        }
      }
      if (!changed) return prev;
      return { ...prev, fuel_cost_tonnage: nextFuel };
    });
  }, [latestFuelPpl, editMode]);

  /** Rows as stored / drafted (vehicle counts by method not yet overlaid). */
  const storedVehicleTypes: FleetCostVehicleType[] = useMemo(() => {
    if (!active) return [];
    if (isEmergency) return active.vehicle_types_emergency;
    return active.vehicle_types_tonnage;
  }, [active, isEmergency]);

  const fleetDefaultByName = useMemo(() => {
    const map = new Map<string, FleetCostVehicleType>();
    if (!fleetDefaults) return map;
    const rows = isEmergency
      ? fleetDefaults.vehicle_types_emergency
      : fleetDefaults.vehicle_types_tonnage;
    for (const row of rows) map.set(row.name, row);
    return map;
  }, [fleetDefaults, isEmergency]);

  const runningRateKeys = useMemo(
    () => PROFITABILITY_TONNAGE_CLASSES as string[],
    [],
  );

  const quantityByName = useMemo(() => {
    if (!assetClassLabels) return {};
    const rowNames = isRunningTab
      ? runningRateKeys
      : storedVehicleTypes.map((v) => v.name);
    return countFleetCostQtyByName({
      assetClassLabels,
      rateBasis: 'tonnage',
      isEmergency: isRunningTab ? false : isEmergency,
      rowNames,
    });
  }, [
    assetClassLabels,
    isEmergency,
    isRunningTab,
    runningRateKeys,
    storedVehicleTypes,
  ]);

  /** Purchase / lease / rental per class from the Assets Acquisition column. */
  const acquisitionMixByName = useMemo(() => {
    if (!assetClassLabels) return null;
    return countFleetCostAcquisitionMixByName({
      assetClassLabels,
      rateBasis: 'tonnage',
      isEmergency,
      rowNames: storedVehicleTypes.map((v) => v.name),
    });
  }, [assetClassLabels, isEmergency, storedVehicleTypes]);

  /** Stamp Assets vehicle counts by method onto class rows (kept as stored if Assets failed). */
  const withAssetsMix = useCallback(
    (rows: FleetCostVehicleType[]): FleetCostVehicleType[] =>
      acquisitionMixByName
        ? rows.map((row) => withAcquisitionMixFromAssets(row, acquisitionMixByName[row.name]))
        : rows,
    [acquisitionMixByName],
  );

  const vehicleTypes: FleetCostVehicleType[] = useMemo(
    () => withAssetsMix(storedVehicleTypes),
    [withAssetsMix, storedVehicleTypes],
  );

  /**
   * Classes shown as table columns. The client runs no 26T vehicles, so the 26T
   * column is hidden while Assets counts none, and while Assets is still
   * loading (it used to flash up first). It shows when Assets has a 26T
   * vehicle, so its costs are never used out of sight, or when Assets could not
   * be read. Display only: the stored config, saves and the CSV export keep
   * every class.
   */
  const hideClass = useCallback(
    (name: string) => name === HIDDEN_WHEN_UNUSED_CLASS
      && !assetLabelsFailed
      && (assetClassLabels == null || (quantityByName[name] ?? 0) === 0),
    [assetClassLabels, assetLabelsFailed, quantityByName],
  );
  const visibleVehicleTypes = useMemo(
    () => vehicleTypes.filter((v) => !hideClass(v.name)),
    [vehicleTypes, hideClass],
  );
  const visibleRunningKeys = useMemo(
    () => runningRateKeys.filter((key) => !hideClass(key)),
    [runningRateKeys, hideClass],
  );

  /** Resolve Assets qtys when quantity_source is assets; manual keeps stored (incl. 0). */
  /** Trailers by Deck on Assets: drives the Single / Double deck quantities once any is set. */
  const deckCounts = useMemo(() => countTrailerDecks(assetClassLabels), [assetClassLabels]);
  const trailerTypesFromAssets = deckCountsApply(deckCounts);
  /** Trailers by Acquisition per deck: the same per-method counts vehicle classes get. */
  const trailerAcquisitionByDeck = useMemo(() => countTrailerAcquisitionByDeck(assetClassLabels), [assetClassLabels]);

  const trailerStanding = useMemo(() => {
    const stored = trailerStandingFromConfig(active);
    return resolveTrailerStandingQtys(stored, {
      articQty: quantityByName['Artic'] ?? 0,
      trailerQty: countTrailerAssets(assetClassLabels),
      deckCounts,
      acquisitionByDeck: trailerAcquisitionByDeck,
    });
  }, [active, quantityByName, assetClassLabels, deckCounts, trailerAcquisitionByDeck]);

  const setDraftRows = (updater: (prev: FleetCostVehicleType[]) => FleetCostVehicleType[]) => {
    setRestoreDefaultsPending(false);
    setConfigDraft((prev) => {
      if (!prev) return prev;
      if (isEmergency) {
        return { ...prev, vehicle_types_emergency: updater(prev.vehicle_types_emergency) };
      }
      return { ...prev, vehicle_types_tonnage: updater(prev.vehicle_types_tonnage) };
    });
  };

  const beginEdit = () => {
    if (editMode) return;
    if (!configData && !profitData) return;
    if (configData) {
      const draft = cloneConfig(configData);
      draft.cost_rate_basis = 'tonnage';
      // Resolve assets qtys into the draft for editing; quantity_source stays assets
      // until the user changes a qty field (then → manual).
      draft.trailer_standing = resolveTrailerStandingQtys(trailerStandingFromConfig(draft), {
        articQty: quantityByName['Artic'] ?? 0,
        trailerQty: countTrailerAssets(assetClassLabels),
        deckCounts,
        acquisitionByDeck: trailerAcquisitionByDeck,
      });
      // Vehicle counts by method come from Assets Acquisition, so a changed
      // mix shows as an unsaved change.
      if (isEmergency) draft.vehicle_types_emergency = withAssetsMix(draft.vehicle_types_emergency);
      else draft.vehicle_types_tonnage = withAssetsMix(draft.vehicle_types_tonnage);
      setConfigDraft(draft);
    }
    if (profitData) {
      const draft = cloneProfitConfig(profitData);
      draft.cost_rate_basis = 'tonnage';
      setProfitDraft(draft);
    }
    setEditMode(true);
    setRestoreDefaultsPending(false);
    setConfigError(null);
    setConfigWarning(null);
  };

  const cancelEdit = () => {
    if (configData) {
      setConfigDraft(cloneConfig(configData));
    }
    if (profitData) {
      setProfitDraft(cloneProfitConfig(profitData));
    }
    setEditMode(false);
    setRestoreDefaultsPending(false);
    setConfigError(null);
    setConfigWarning(null);
  };

  const revertEdit = () => {
    if (configData) {
      setConfigDraft(cloneConfig(configData));
    }
    if (profitData) {
      setProfitDraft(cloneProfitConfig(profitData));
    }
    setRestoreDefaultsPending(false);
    setConfigError(null);
    setConfigWarning(null);
  };

  /**
   * Restore the active tab's figures to the server's built-in defaults
   * (per vehicle class — a Van and a 44T do not share a default price).
   * Rows with no matching default class (custom rows) are left unchanged.
   * Only the draft changes; nothing persists until Save.
   */
  const resetDefaults = async () => {
    setConfigError(null);
    try {
      if (activeTab === 'running') {
        const defaults = await fetchTruthProfitabilityConfigDefaults();
        setProfitDraft((prev) => {
          if (!prev) return prev;
          const next = cloneProfitConfig(prev);
          next.fuel_cost_tonnage = { ...next.fuel_cost_tonnage, ...defaults.fuel_cost_tonnage } as typeof next.fuel_cost_tonnage;
          // Defaults have no MPG table — clear so MPG does not disagree with restored £/mi.
          next.fuel_mpg_tonnage = defaults.fuel_mpg_tonnage
            ? { ...defaults.fuel_mpg_tonnage }
            : {};
          next.tyre_cost_tonnage = { ...next.tyre_cost_tonnage, ...defaults.tyre_cost_tonnage } as typeof next.tyre_cost_tonnage;
          next.variable_repair_cost_tonnage = { ...next.variable_repair_cost_tonnage, ...defaults.variable_repair_cost_tonnage } as typeof next.variable_repair_cost_tonnage;
          next.driver_cost_tonnage = { ...next.driver_cost_tonnage, ...defaults.driver_cost_tonnage } as typeof next.driver_cost_tonnage;
          return next;
        });
        setRestoreDefaultsPending(true);
        return;
      }

      const defaults = await fetchFleetCostAssumptionsDefaults();
      const defaultRows = isEmergency ? defaults.vehicle_types_emergency : defaults.vehicle_types_tonnage;
      const defaultByName = new Map(defaultRows.map((row) => [row.name, row]));
      const fields: ReadonlyArray<keyof FleetCostVehicleType> =
        activeTab === 'capital'
          ? CAPITAL_FIELDS
          : [
              'insurance',
              'roadTax',
              'oLicense',
              'telematics',
              'cleaning',
              'parking',
              'permits',
              'scheduledMaintenance',
              'vehicleCompliance',
              'overhead',
            ];

      setConfigDraft((prev) => {
        if (!prev) return prev;
        const rows = (isEmergency ? prev.vehicle_types_emergency : prev.vehicle_types_tonnage).map((row) => {
          const def = defaultByName.get(row.name);
          if (!def) return row;
          const restored: FleetCostVehicleType = { ...row };
          for (const field of fields) {
            (restored as unknown as Record<string, unknown>)[field] = def[field];
          }
          return restored;
        });
        return isEmergency
          ? { ...prev, vehicle_types_emergency: rows }
          : { ...prev, vehicle_types_tonnage: rows };
      });
      setRestoreDefaultsPending(true);
    } catch (err) {
      setConfigError(err instanceof Error ? err.message : 'Failed to load default figures');
    }
  };

  const saveEdit = async () => {
    const fleetDirty = isFleetCostDraftDirty(configDraft, configData);
    const profitDirty = isProfitabilityRatesDraftDirty(profitDraft, profitData);

    // Assets overlay must not stamp zeros when the roster failed to load.
    if (fleetDirty && configDraft) {
      const qs = trailerStandingFromConfig(configDraft).quantity_source ?? 'assets';
      if (qs === 'assets' && assetClassLabels === null && !assetQtyLoading) {
        setConfigError(
          'Could not load Assets quantities. Refresh the page before saving trailer standing.',
        );
        return;
      }
    }

    setConfigSaving(true);
    setConfigError(null);
    setConfigWarning(null);
    try {
      const actor = await resolveActorEmail();
      const saveOpts =
        restoreDefaultsPending && isSessionAdmin()
          ? { saveReason: 'reset_to_defaults' as const }
          : undefined;
      let standingSyncWarning: string | null = null;
      let latestFleet = configDraft ?? configData;

      // Serialise: fleet first (server materialises Standing £/day), then profitability
      // running rates — avoids parallel PUT races that can drop fuel/driver edits.
      if (fleetDirty && configDraft) {
        const payload: FleetCostAssumptionsConfig = {
          ...configDraft,
          cost_rate_basis: 'tonnage',
          use_case: isEmergency ? 'emergency' : 'logistics',
          ...(isEmergency
            ? { vehicle_types_emergency: withAssetsMix(configDraft.vehicle_types_emergency) }
            : { vehicle_types_tonnage: withAssetsMix(configDraft.vehicle_types_tonnage) }),
          // Stamp resolved Assets qtys; quantities are never user-editable here.
          trailer_standing: resolveTrailerStandingQtys(
            { ...trailerStandingFromConfig(configDraft), quantity_source: 'assets' },
            {
              articQty: quantityByName['Artic'] ?? 0,
              trailerQty: countTrailerAssets(assetClassLabels),
            },
          ),
          updated_by: actor,
          updated_at: new Date().toISOString(),
        };
        const saved = await saveFleetCostAssumptionsConfig(payload, saveOpts);
        const {
          standing_sync_ok: syncOk,
          standing_sync_error: syncErr,
          ...fleetSaved
        } = saved;
        latestFleet = fleetSaved;
        setConfigData(fleetSaved);
        setConfigDraft(cloneConfig(fleetSaved));
        if (syncOk === false) {
          standingSyncWarning =
            syncErr
            || 'Standing rates saved, but Profitability Standing (£/day) sync failed. Retry Save.';
          setStandingSyncPending(true);
        } else {
          setStandingSyncPending(false);
        }
        notifyProfitabilityConfigUpdated();
      }

      // Re-stamp Standing £/day when fuel/driver dirty, when server standing sync just
      // failed, or when an earlier save's sync is still pending (fleetDirty is false on
      // retry since the drafts already reflect the saved fleet data).
      const profitBase = profitDraft ?? (profitData ? cloneProfitConfig(profitData) : null);
      const shouldSaveProfit =
        Boolean(profitBase)
        && (profitDirty || standingSyncWarning != null || standingSyncPending);

      if (shouldSaveProfit && profitBase) {
        let payload: ProfitabilityAssumptionsConfig = {
          ...profitBase,
          cost_rate_basis: 'tonnage',
          updated_by: actor,
          updated_at: new Date().toISOString(),
        };
        if (latestFleet) {
          // latestFleet is the complete, current fleet-cost config, so derived
          // already enumerates every vehicle type name that exists today —
          // replace rather than merge, so a renamed/deleted vehicle type's
          // stale £/day entry doesn't linger.
          const derived = deriveStandingPerDayMaps(latestFleet);
          payload = {
            ...payload,
            depreciation: derived.body_style as ProfitabilityAssumptionsConfig['depreciation'],
            depreciation_tonnage: derived.tonnage as ProfitabilityAssumptionsConfig['depreciation_tonnage'],
          };
        }
        const saved = await saveTruthProfitabilityConfig(payload, saveOpts);
        setProfitData(saved);
        setProfitDraft(cloneProfitConfig(saved));
        notifyProfitabilityConfigUpdated();
        standingSyncWarning = null;
        setStandingSyncPending(false);
      }

      if (standingSyncWarning) {
        // Stay in edit mode so Retry Save can re-stamp standing.
        setConfigWarning(standingSyncWarning);
      } else {
        setRestoreDefaultsPending(false);
        setEditMode(false);
      }
    } catch (err) {
      setConfigError(err instanceof Error ? err.message : 'Failed to save cost assumptions');
    } finally {
      setConfigSaving(false);
    }
  };

  const vehicleConfirmTable = isEmergency ? 'emergency' : 'tonnage';

  const confirmationsForActiveTab = useMemo((): FleetCostRateConfirmations => {
    if (isRunningTab) return activeProfit?.rate_confirmations ?? {};
    return active?.rate_confirmations ?? {};
  }, [active, activeProfit, isRunningTab]);

  const collectTabOverrides = useCallback((): FleetCostRateConfirmations => {
    const overrides: FleetCostRateConfirmations = {};
    if (activeTab === 'running') {
      if (!profitData) return overrides;
      const fuel = (profitData.fuel_cost_tonnage ?? {}) as Record<string, number | undefined>;
      const tyre = (profitData.tyre_cost_tonnage ?? {}) as Record<string, number | undefined>;
      const repair = (profitData.variable_repair_cost_tonnage ?? {}) as Record<
        string,
        number | undefined
      >;
      const driver = (profitData.driver_cost_tonnage ?? {}) as Record<
        string,
        ProfitabilityDriverCostRate | undefined
      >;
      const fuelDef = (profitDefaults?.fuel_cost_tonnage ?? {}) as Record<string, number | undefined>;
      const tyreDef = (profitDefaults?.tyre_cost_tonnage ?? {}) as Record<string, number | undefined>;
      const repairDef = (profitDefaults?.variable_repair_cost_tonnage ?? {}) as Record<
        string,
        number | undefined
      >;
      const driverDef = (profitDefaults?.driver_cost_tonnage ?? {}) as Record<
        string,
        ProfitabilityDriverCostRate | undefined
      >;
      for (const key of PROFITABILITY_TONNAGE_CLASSES) {
        const pairs: Array<{
          map: string;
          current: number;
          def: number | undefined;
          sub?: string;
        }> = [
          { map: 'fuel_cost_tonnage', current: fuel[key] ?? 0, def: fuelDef[key] },
          { map: 'tyre_cost_tonnage', current: tyre[key] ?? 0, def: tyreDef[key] },
          {
            map: 'variable_repair_cost_tonnage',
            current: repair[key] ?? 0,
            def: repairDef[key],
          },
          {
            map: 'driver_cost_tonnage',
            current: driver[key]?.mileage_rate ?? 0,
            def: driverDef[key]?.mileage_rate,
            sub: 'mileage_rate',
          },
          {
            map: 'driver_cost_tonnage',
            current: driver[key]?.driving_hourly_rate ?? 0,
            def: driverDef[key]?.driving_hourly_rate,
            sub: 'driving_hourly_rate',
          },
          {
            map: 'driver_cost_tonnage',
            current: driver[key]?.waiting_hourly_rate ?? 0,
            def: driverDef[key]?.waiting_hourly_rate,
            sub: 'waiting_hourly_rate',
          },
        ];
        for (const pair of pairs) {
          if (isFleetCostValueOverridden(pair.current, pair.def)) {
            overrides[fleetCostConfirmKeyRunning(pair.map, key, pair.sub)] = pair.current;
          }
        }
      }
      return overrides;
    }

    if (!configData) return overrides;
    const rows = isEmergency
      ? configData.vehicle_types_emergency
      : configData.vehicle_types_tonnage;
    const fields = activeTab === 'capital' ? CAPITAL_FIELDS : OPERATIONAL_FIELDS;
    for (const row of rows) {
      const defRow = fleetDefaultByName.get(row.name);
      for (const field of fields) {
        const raw = row[field];
        const fallback =
          activeTab === 'operational'
            ? FLEET_STANDING_OP_DEFAULTS[field as keyof typeof FLEET_STANDING_OP_DEFAULTS]
            : undefined;
        const current =
          typeof raw === 'number'
            ? raw
            : typeof fallback === 'number'
              ? fallback
              : typeof defRow?.[field] === 'number'
                ? (defRow[field] as number)
                : 0;
        const defaultValue =
          typeof defRow?.[field] === 'number'
            ? (defRow[field] as number)
            : typeof fallback === 'number'
              ? fallback
              : undefined;
        if (isFleetCostValueOverridden(current, defaultValue)) {
          overrides[fleetCostConfirmKeyVehicle(vehicleConfirmTable, row.name, field)] = current;
        }
      }
    }

    if (activeTab === 'operational' && !isEmergency) {
      const trailer = trailerStandingFromConfig(configData);
      const rate = trailer.standing_gbp_per_trailer_year;
      if (isFleetCostValueOverridden(rate, DEFAULT_TRAILER_STANDING_GBP_PER_YEAR)) {
        overrides[fleetCostConfirmKeyTrailer('standing_gbp_per_trailer_year')] = rate;
      }
    }
    return overrides;
  }, [
    activeTab,
    configData,
    fleetDefaultByName,
    isEmergency,
    profitData,
    profitDefaults,
    vehicleConfirmTable,
  ]);

  const hasConfirmableOverrides = useMemo(() => {
    if (editMode) return false;
    const overrides = collectTabOverrides();
    for (const [key, value] of Object.entries(overrides)) {
      const stamped = confirmationsForActiveTab[key];
      if (stamped == null || Math.abs(stamped - value) > 1e-9) return true;
    }
    return false;
  }, [collectTabOverrides, confirmationsForActiveTab, editMode]);

  const confirmRates = async () => {
    if (editMode) {
      setConfigError('Save or cancel your edits before confirming rates.');
      return;
    }
    const overrides = collectTabOverrides();
    const confirmableEntries = Object.entries(overrides).filter(([key, value]) => {
      const stamped = confirmationsForActiveTab[key];
      return stamped == null || Math.abs(stamped - value) > 1e-9;
    });
    if (confirmableEntries.length === 0) {
      setConfigError('Nothing to confirm on this tab — change a rate from its estimate first.');
      return;
    }

    setConfigSaving(true);
    setConfigError(null);
    setConfigWarning(null);
    setRestoreDefaultsPending(false);
    try {
      const actor = await resolveActorEmail();
      if (activeTab === 'running') {
        if (!profitData) throw new Error('Profitability assumptions are not loaded');
        const retainKey = (key: string) => key.startsWith('r.');
        const rate_confirmations = mergeTabConfirmations({
          existing: profitData.rate_confirmations,
          overridesToConfirm: overrides,
          retainKey,
        });
        const saved = await saveTruthProfitabilityConfig({
          ...profitData,
          rate_confirmations,
          updated_by: actor,
          updated_at: new Date().toISOString(),
        });
        const withConfirm: ProfitabilityAssumptionsConfig = {
          ...saved,
          rate_confirmations:
            saved.rate_confirmations && Object.keys(saved.rate_confirmations).length > 0
              ? saved.rate_confirmations
              : rate_confirmations,
        };
        setProfitData(withConfirm);
        setProfitDraft(cloneProfitConfig(withConfirm));
        // Do not notify siblings — confirm only stamps trust, it does not change
        // rates. A refetch race was wiping green back to blue.
      } else {
        if (!configData) throw new Error('Fleet cost assumptions are not loaded');
        const retainKey = (key: string) => {
          if (activeTab === 'operational' && key.startsWith('trailer.')) return true;
          if (!key.startsWith(`v.${vehicleConfirmTable}.`)) return false;
          const field = key.split('.').slice(3).join('.');
          return activeTab === 'capital'
            ? CAPITAL_FIELD_SET.has(field)
            : OPERATIONAL_FIELD_SET.has(field);
        };
        const rate_confirmations = mergeTabConfirmations({
          existing: configData.rate_confirmations,
          overridesToConfirm: overrides,
          retainKey,
        });
        const saved = await saveFleetCostAssumptionsConfig({
          ...configData,
          rate_confirmations,
          updated_by: actor,
          updated_at: new Date().toISOString(),
        });
        const {
          standing_sync_ok: _syncOk,
          standing_sync_error: _syncErr,
          ...fleetSaved
        } = saved;
        const withConfirm: FleetCostAssumptionsConfig = {
          ...fleetSaved,
          rate_confirmations:
            fleetSaved.rate_confirmations && Object.keys(fleetSaved.rate_confirmations).length > 0
              ? fleetSaved.rate_confirmations
              : rate_confirmations,
        };
        setConfigData(withConfirm);
        setConfigDraft(cloneConfig(withConfirm));
      }
    } catch (err) {
      setConfigError(err instanceof Error ? err.message : 'Failed to confirm rates');
    } finally {
      setConfigSaving(false);
    }
  };

  const qtyDisplay = (name: string): string => {
    if (assetQtyLoading) return '…';
    if (assetClassLabels === null) return '—';
    return String(quantityByName[name] ?? 0);
  };

  const updateField = (vehicleId: string, field: string, value: string) => {
    const num = parseNonNegativeNumber(value);
    setDraftRows((prev) => prev.map((v) => (v.id === vehicleId ? { ...v, [field]: num } : v)));
  };

  /** Non-numeric capital fields (method, rental period, maintenance flag). */
  const updateVehicleRow = (vehicleId: string, patch: Partial<FleetCostVehicleType>) => {
    setDraftRows((prev) => prev.map((v) => (v.id === vehicleId ? { ...v, ...patch } : v)));
  };

  const setRunningFuel = (key: string, value: number) => {
    setRestoreDefaultsPending(false);
    setProfitDraft((prev) => {
      if (!prev) return prev;
      return {
        ...prev,
        fuel_cost_tonnage: {
          ...(prev.fuel_cost_tonnage ?? ({} as Record<ProfitabilityTonnageClass, number>)),
          [key as ProfitabilityTonnageClass]: value,
        },
      };
    });
  };

  /** Persist MPG and, when a weekly PPL is available, rewrite Fuel £/mi for that class. */
  const setRunningMpg = (key: string, mpg: number) => {
    setRestoreDefaultsPending(false);
    setProfitDraft((prev) => {
      if (!prev) return prev;
      const nextMpg = {
        ...(prev.fuel_mpg_tonnage ?? {}),
        [key as ProfitabilityTonnageClass]: mpg,
      };
      const nextFuel = {
        ...(prev.fuel_cost_tonnage ?? ({} as Record<ProfitabilityTonnageClass, number>)),
      };
      if (latestFuelPpl != null && mpg > 0) {
        const derived = gbpPerMileFromMpgAndPpl(mpg, latestFuelPpl);
        if (derived != null) {
          nextFuel[key as ProfitabilityTonnageClass] = derived;
        }
      }
      return {
        ...prev,
        fuel_mpg_tonnage: nextMpg,
        fuel_cost_tonnage: nextFuel,
      };
    });
  };

  const setRunningMileageRate = (
    mapKey: 'tyre_cost_tonnage' | 'variable_repair_cost_tonnage',
    key: string,
    value: number,
  ) => {
    setRestoreDefaultsPending(false);
    setProfitDraft((prev) => {
      if (!prev) return prev;
      return {
        ...prev,
        [mapKey]: {
          ...(prev[mapKey] ?? ({} as Record<ProfitabilityTonnageClass, number>)),
          [key as ProfitabilityTonnageClass]: value,
        },
      };
    });
  };

  const setRunningDriver = (
    key: string,
    field: keyof ProfitabilityDriverCostRate,
    value: number,
  ) => {
    setRestoreDefaultsPending(false);
    setProfitDraft((prev) => {
      if (!prev) return prev;
      const current = prev.driver_cost_tonnage?.[key as ProfitabilityTonnageClass] ?? {
        mileage_rate: 0,
        driving_hourly_rate: 0,
        waiting_hourly_rate: 0,
      };
      return {
        ...prev,
        driver_cost_tonnage: {
          ...(prev.driver_cost_tonnage ??
            ({} as Record<ProfitabilityTonnageClass, ProfitabilityDriverCostRate>)),
          [key as ProfitabilityTonnageClass]: { ...current, [field]: value },
        },
      };
    });
  };

  const setTrailerStandingField = <K extends keyof TrailerStandingAssumptions>(
    field: K,
    value: TrailerStandingAssumptions[K],
  ) => {
    setRestoreDefaultsPending(false);
    setConfigDraft((prev) => {
      if (!prev) return prev;
      const current = trailerStandingFromConfig(prev);
      return {
        ...prev,
        trailer_standing: {
          ...current,
          [field]: value,
          // Quantities always track Assets — never lock to manual via this UI.
          quantity_source: 'assets',
        },
      };
    });
  };

  // Trailer types (single / double deck) are columns beside the tonnage classes.
  // A one-rate pool shows the split's starting point (every trailer on Single
  // deck at the one rate, so the pool is unchanged); the first edit of a trailer
  // cell stores that split for real.
  const trailerSplit = (trailerStanding.trailer_types ?? []).length > 0;
  const trailerCols: TrailerTypeRow[] = isEmergency
    ? []
    : trailerSplit
      ? trailerStanding.trailer_types!
      : withTrailerAcquisitionMix(
          withDeckQuantities(
            defaultTrailerTypes(trailerStanding.standing_gbp_per_trailer_year, trailerStanding.trailer_quantity),
            deckCounts,
          ),
          trailerAcquisitionByDeck,
        );
  // Single / Double deck quantities come from Assets once any trailer has a Deck.
  const trailerQtyFromAssets = (t: TrailerTypeRow) =>
    trailerTypesFromAssets && (t.id === 'single-deck' || t.id === 'double-deck');
  const updateTrailerType = (id: string, patch: Partial<TrailerTypeRow>) =>
    setTrailerStandingField(
      'trailer_types',
      trailerCols.map((t) => (t.id === id ? { ...t, ...patch } : t)),
    );
  const removeTrailerType = (id: string) => {
    const rest = trailerCols.filter((t) => t.id !== id);
    setTrailerStandingField('trailer_types', rest.length > 0 ? rest : undefined);
  };
  const addTrailerTypeButton = (
    <button
      type="button"
      onClick={() =>
        setTrailerStandingField('trailer_types', [
          ...trailerCols,
          {
            id: `trailer-type-${Date.now()}`,
            name: `Trailer type ${trailerCols.length + 1}`,
            quantity: 0,
            standing_gbp_per_trailer_year: trailerCols[trailerCols.length - 1]?.standing_gbp_per_trailer_year ?? 0,
            acquisitionMethod: 'lease',
            leaseMonthly: 0,
            leaseTermMonths: 60,
          },
        ])
      }
      className="inline-flex items-center gap-1 text-xs text-amber-400 hover:text-amber-300"
    >
      <Plus className="w-3.5 h-3.5" /> Trailer type
    </button>
  );
  const notUsedTd = (key: string, title = 'Not used for trailers') => (
    <td key={key} className={`${valueTdClass} text-gray-600`} title={title}>
      n/a
    </td>
  );
  /** A trailer value; one never entered shows "Not entered", never a borrowed vehicle default. */
  const trailerNumberCell = (
    t: TrailerTypeRow,
    field: keyof TrailerTypeRow,
    value: number | undefined,
    display: (n: number) => ReactNode,
    step: number | string,
  ) => {
    const known = typeof value === 'number' && Number.isFinite(value);
    return (
      <td key={t.id} className={`${valueTdClass} ${known ? 'text-gray-200' : 'text-gray-500'}`}>
        {editMode ? (
          <input
            type="number"
            min={0}
            step={step}
            value={known ? value : ''}
            placeholder="Not entered"
            onChange={(e) =>
              updateTrailerType(t.id, {
                [field]: e.target.value.trim() === '' ? undefined : parseNonNegativeNumber(e.target.value),
              })
            }
            className={inputClass}
            aria-label={`${t.name} ${String(field)}`}
          />
        ) : known ? (
          display(value)
        ) : (
          'Not entered'
        )}
      </td>
    );
  };
  /** The rates a method needs before its capital means anything (unknown is not £0). */
  const trailerCapitalMissing = (t: TrailerTypeRow): string | null => {
    const has = (n: unknown) => typeof n === 'number' && Number.isFinite(n) && n > 0;
    for (const m of acquisitionMethodsInUse(t)) {
      if (m === 'purchase' && !(has(t.unitCost) && has(t.serviceLife))) return 'Purchase price and useful life not entered';
      if (m === 'lease' && !has(t.leaseMonthly)) return 'Lease £/month not entered';
      if (m === 'rental' && !has(t.rentalRate)) return 'Rental rate not entered';
    }
    return null;
  };
  /** Single and Double deck are fixed (Assets Deck counts drive them); added types can be renamed or removed. */
  const isBuiltInTrailerType = (t: TrailerTypeRow) => t.id === 'single-deck' || t.id === 'double-deck';
  const trailerTh = (t: TrailerTypeRow, cls: string) => (
    <th key={t.id} className={cls}>
      <span className="block text-xs font-normal uppercase tracking-wide text-amber-400/80">Trailer</span>
      {editMode && !isBuiltInTrailerType(t) ? (
        <span className="inline-flex items-center gap-1">
          <input
            type="text"
            value={t.name}
            onChange={(e) => updateTrailerType(t.id, { name: e.target.value })}
            className={`${inputClass} w-28`}
            aria-label="Trailer type name"
          />
          {trailerSplit ? (
          <button
            type="button"
            onClick={() => removeTrailerType(t.id)}
            className="text-gray-400 hover:text-red-400"
            title={`Remove ${t.name}`}
            aria-label={`Remove ${t.name}`}
          >
            <Trash2 className="w-4 h-4" />
          </button>
          ) : null}
        </span>
      ) : (
        t.name
      )}
    </th>
  );

  const runningFuelMap = activeProfit?.fuel_cost_tonnage ?? {};
  const runningMpgMap = activeProfit?.fuel_mpg_tonnage ?? {};
  const runningTyreMap = activeProfit?.tyre_cost_tonnage ?? {};
  const runningRepairMap = activeProfit?.variable_repair_cost_tonnage ?? {};
  const runningDriverMap = activeProfit?.driver_cost_tonnage ?? {};

  const runningFieldInfo = useMemo(
    (): Record<string, ReactNode> => ({
      'Fuel (MPG)': fuelMpgFieldInfo(latestFuelPpl, latestFuelPplWeek),
      'Fuel (£/mi)': FUEL_GBP_PER_MI_FIELD_INFO,
    }),
    [latestFuelPpl, latestFuelPplWeek],
  );

  const metaVersion = isRunningTab ? activeProfit?.version : active?.version;
  const metaUpdatedAt = isRunningTab ? activeProfit?.updated_at : active?.updated_at;
  const metaUpdatedBy = isRunningTab ? activeProfit?.updated_by : active?.updated_by;

  const resolveActorEmail = async (): Promise<string> => {
    const fromToken = readSessionUserEmail();
    if (fromToken) return fromToken;
    const fromSession = await getCurrentUserEmail();
    return fromSession?.trim() || 'user';
  };

  const inputClass =
    'w-20 min-w-20 max-w-20 px-2 py-1 bg-background border-2 border-accent rounded text-xs text-right text-foreground focus:outline-none focus:ring-2 focus:ring-warning focus:border-warning';

  /** Scroll panes (all three tables): freeze header row + field column corner. */
  const stickyFieldThFrozen =
    'sticky top-0 left-0 z-30 text-left py-3 px-4 text-foreground font-medium border-b border-r border-accent bg-card/95 shadow-panel';
  const stickyClassThFrozen =
    'sticky top-0 z-20 text-right py-3 px-4 text-foreground font-medium border-b border-accent bg-background/90 whitespace-nowrap';
  const stickyFieldTd =
    'sticky left-0 z-10 py-3 px-4 text-foreground font-medium border-r border-accent bg-card/95 group-hover:bg-secondary shadow-panel';
  const valueTdClass = 'py-3 px-4 text-right tabular-nums';
  const capitalSelectClass =
    'px-2 py-1 bg-gray-900 border-2 border-gray-600 rounded text-xs text-gray-100 focus:outline-none focus:ring-2 focus:ring-amber-500';
  const capitalGroupTd =
    'sticky left-0 z-10 pt-4 pb-1 px-4 text-xs font-semibold uppercase tracking-wide text-gray-400 border-r border-gray-600 bg-slate-900/95';
  const totalRowLabelTd =
    'sticky left-0 z-30 py-3 px-4 text-success font-semibold border-r border-t border-success/70 bg-success-surface/90 group-hover:bg-success-surface-hover shadow-panel';
  const totalRowValueTd =
    'py-3 px-4 text-right text-success font-semibold tabular-nums border-t border-success/70 bg-success-surface/90';

  const resolveDefaultVehicleField = (
    vehicleName: string,
    field: keyof FleetCostVehicleType,
    fallback?: number,
  ): number | undefined => {
    const defRow = fleetDefaultByName.get(vehicleName);
    if (!defRow) return undefined;
    const raw = defRow[field];
    if (typeof raw === 'number') return raw;
    return fallback;
  };

  const rateValueCell = (
    current: number,
    defaultValue: number | undefined,
    opts?: {
      confirmKey?: string;
      formatDefault?: (n: number) => string;
      edit?: { step: number | string; onChange: (raw: string) => void };
      display?: ReactNode;
      /** No vehicle in the class uses this acquisition method: the rate adds no cost yet, so it is dimmed. */
      unused?: boolean;
    },
  ) => {
    const confirmedValue =
      opts?.confirmKey != null ? confirmationsForActiveTab[opts.confirmKey] : undefined;
    const status: FleetCostValueStatus = resolveFleetCostValueStatus(
      current,
      defaultValue,
      confirmedValue,
    );
    const title = opts?.unused
      ? `${UNUSED_METHOD_TITLE} ${fleetCostValueTitle(status, defaultValue, opts?.formatDefault)}`
      : fleetCostValueTitle(status, defaultValue, opts?.formatDefault);
    if (opts?.edit) {
      return (
        <td
          className={`${valueTdClass} ${opts.unused ? 'opacity-50' : ''}`}
          title={title}
          data-override={status !== 'estimated' ? 'true' : 'false'}
          data-status={status}
        >
          <input
            type="number"
            step={opts.edit.step}
            min={0}
            value={current}
            onChange={(e) => opts.edit!.onChange(e.target.value)}
            className={fleetCostValueInputClass(status, inputClass)}
            aria-label={title}
          />
        </td>
      );
    }
    return (
      <td
        className={`${valueTdClass} ${opts?.unused ? 'text-gray-600' : fleetCostValueTextClass(status)}`}
        title={title}
        data-override={status !== 'estimated' ? 'true' : 'false'}
        data-status={status}
      >
        {opts?.display ?? current}
      </td>
    );
  };

  const valueLegend = (
    <p className="text-xs text-muted-foreground mb-3 flex flex-wrap items-center gap-x-4 gap-y-1" role="note">
      <span className="inline-flex items-center gap-1.5">
        <span className="w-2 h-2 rounded-sm bg-warning" aria-hidden />
        <span className="text-warning/90">Amber = Estimated</span>
      </span>
      <span className="inline-flex items-center gap-1.5">
        <span className="w-2 h-2 rounded-sm bg-ring" aria-hidden />
        <span className="text-ring/90">Blue = Asserted</span>
      </span>
      <span className="inline-flex items-center gap-1.5">
        <span className="w-2 h-2 rounded-sm bg-success" aria-hidden />
        <span className="text-success/90">Green = Asserted · confirmed</span>
      </span>
    </p>
  );

  const editActions = (
    <div className="flex items-center gap-2 shrink-0">
      {!editMode ? (
        <>
          <button
            type="button"
            onClick={() => beginEdit()}
            disabled={isRunningTab ? !profitData : !configData}
            title="Start editing all values in this table"
            className="px-2.5 py-1 text-xs font-medium bg-warning hover:bg-warning disabled:opacity-50 text-white rounded transition-colors"
          >
            Edit All
          </button>
          <button
            type="button"
            onClick={() => void confirmRates()}
            disabled={configSaving || !hasConfirmableOverrides}
            title={
              editMode
                ? 'Save or cancel edits before confirming'
                : hasConfirmableOverrides
                  ? 'Mark blue asserted rates on this tab as confirmed (green)'
                  : 'No unconfirmed asserted rates on this tab'
            }
            className="px-2.5 py-1 text-xs font-medium bg-primary hover:bg-primary disabled:opacity-50 text-white rounded flex items-center gap-1 transition-colors"
          >
            <Check className="w-3 h-3" aria-hidden />
            {configSaving ? 'Confirming…' : 'Confirm rates'}
          </button>
        </>
      ) : (
        <>
          <button
            type="button"
            disabled={configSaving}
            onClick={revertEdit}
            title="Restore the last saved version of this table"
            className="px-2.5 py-1 text-xs font-medium bg-ring hover:bg-ring disabled:opacity-50 text-white rounded transition-colors"
          >
            Revert
          </button>
          <button
            type="button"
            disabled={configSaving}
            onClick={resetDefaults}
            title="Reset all values in this table to the default figures"
            className="px-2.5 py-1 text-xs font-medium bg-destructive hover:bg-destructive disabled:opacity-50 text-white rounded transition-colors"
          >
            Reset
          </button>
          <button
            type="button"
            disabled={
              configSaving
              || (
                (trailerStanding.quantity_source ?? 'assets') === 'assets'
                && assetClassLabels === null
                && !assetQtyLoading
              )
            }
            title={
              (trailerStanding.quantity_source ?? 'assets') === 'assets'
              && assetClassLabels === null
              && !assetQtyLoading
                ? 'Assets quantities unavailable — refresh before saving'
                : 'Save the current edits to the persisted fleet assumptions'
            }
            onClick={() => void saveEdit()}
            className="px-2.5 py-1 text-xs font-medium bg-primary hover:bg-primary disabled:opacity-50 text-white rounded flex items-center gap-1 transition-colors"
          >
            <Save className="w-3 h-3" />
            {configSaving ? 'Saving…' : 'Save'}
          </button>
          <button
            type="button"
            disabled={configSaving}
            onClick={cancelEdit}
            title="Discard the current unsaved edits and keep the last saved version"
            className="px-2.5 py-1 text-xs font-medium bg-accent hover:bg-accent disabled:opacity-50 text-foreground rounded transition-colors"
          >
            Cancel
          </button>
        </>
      )}
      <button
        type="button"
        disabled={!configData || vehicleTypes.length === 0}
        onClick={() =>
          exportFleetCostAssumptionsCsv({
            vehicleTypes,
            quantityByName,
            trailerStanding,
            profit: activeProfit,
            includeTrailerPool: !isEmergency,
            runningClassKeys: runningRateKeys,
          })
        }
        title="Download Capital, Standing, and Running rate tables as CSV"
        className="px-2.5 py-1 text-xs font-medium bg-accent hover:bg-accent disabled:opacity-50 text-white rounded flex items-center gap-1 transition-colors"
      >
        <Download className="w-3 h-3" aria-hidden />
        Export All
      </button>
    </div>
  );

  if (configLoading && !configData) {
    return (
      <div className="bg-card border border-accent rounded-lg overflow-hidden px-4 py-6 text-sm text-muted-foreground">
        Loading fleet cost assumptions…
      </div>
    );
  }

  if (!configData || !active) {
    return (
      <div className="bg-card border border-accent rounded-lg overflow-hidden px-4 py-6 space-y-3">
        <p className="text-sm text-destructive">{configError ?? 'Fleet cost assumptions unavailable.'}</p>
        <button
          type="button"
          onClick={() => void fetchConfig()}
          className="px-2.5 py-1 text-xs font-medium bg-accent hover:bg-accent text-white rounded"
        >
          Retry
        </button>
      </div>
    );
  }

  return (
    <div className="bg-card border border-accent rounded-lg">
      <div className="flex items-center justify-between px-4 py-2.5 bg-secondary/50 border-b border-accent rounded-t-lg">
        <div className="flex items-center gap-2 min-w-0">
          <Settings className="w-3.5 h-3.5 text-success shrink-0" />
          <span className="text-xs font-semibold text-foreground">Vehicle Cost Rates</span>
          <ConfigMetaBadge variant="version" version={metaVersion} className="ml-2" />
        </div>
      </div>

      <div className="p-4 sm:p-6 overflow-visible">
      {configError && (
        <p className="text-sm text-destructive mb-2" role="alert">
          {configError}
        </p>
      )}
      {configWarning && (
        <p className="text-sm text-warning mb-2" role="status">
          {configWarning}
        </p>
      )}
      <p className="text-sm text-muted-foreground mb-4">
        Purchase, standing, and running cost rates
        {isEmergency
          ? ' by appliance type (running rates follow Costs assumptions).'
          : ' by tonnage class.'}
      </p>

      {/* Summary — all-in £/yr per type */}
      <div className="flex flex-wrap gap-x-6 gap-y-3 mb-4">
        {visibleVehicleTypes.map((v) => (
          <div key={v.id} className="text-xs text-muted-foreground">
            <span className="inline-flex items-center">
              {v.name} — all-in £/yr
              <AllInSummaryTip vehicle={v} trailer={trailerStanding} />
            </span>
            <b className="block text-base text-white font-mono mt-0.5 tabular-nums">
              {formatFleetMoneyK(allInAnnualDisplayed(v, trailerStanding))}
            </b>
          </div>
        ))}
      </div>

      {/* Cost-table tabs */}
      <div className="flex flex-wrap items-end justify-start gap-3 mb-4">
        <div
          className="inline-flex p-0.5 rounded-lg border border-accent bg-background/50 shrink-0"
          role="tablist"
          aria-label="Cost rate tables"
        >
          <button
            type="button"
            role="tab"
            aria-selected={activeTab === 'capital'}
            onClick={() => setActiveTab('capital')}
            className={`px-3.5 py-1.5 text-sm font-semibold rounded-md transition-colors ${
              activeTab === 'capital'
                ? 'bg-success text-background'
                : 'bg-transparent text-foreground hover:text-white'
            }`}
          >
            Capital Costs
          </button>
          <button
            type="button"
            role="tab"
            aria-selected={activeTab === 'operational'}
            onClick={() => setActiveTab('operational')}
            className={`px-3.5 py-1.5 text-sm font-semibold rounded-md transition-colors ${
              activeTab === 'operational'
                ? 'bg-success text-background'
                : 'bg-transparent text-foreground hover:text-white'
            }`}
          >
            Standing Costs
          </button>
          <button
            type="button"
            role="tab"
            aria-selected={activeTab === 'running'}
            onClick={() => setActiveTab('running')}
            className={`px-3.5 py-1.5 text-sm font-semibold rounded-md transition-colors ${
              activeTab === 'running'
                ? 'bg-success text-background'
                : 'bg-transparent text-foreground hover:text-white'
            }`}
          >
            Running Costs
          </button>
        </div>
      </div>

      {activeTab === 'capital' && (
        <div role="tabpanel">
          <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
            <p className="text-sm text-muted-foreground min-w-0 flex-1">
              Vehicle counts by method come from the Acquisition column on Assets; enter each
              method's rates here. Total is the average per vehicle, weighted by the counts.
              Vehicles with no Acquisition set count as purchased.
            </p>
            {editMode && trailerCols.length > 0 ? addTrailerTypeButton : null}
            {editActions}
          </div>
          {valueLegend}
          <div className="max-h-table-viewport overflow-x-auto overflow-y-auto rounded border border-gray-700/60">
            <table className="w-full table-fixed text-sm border-separate border-spacing-0">
              <thead>
                <tr>
                  <th className={stickyFieldThFrozen}>Field</th>
                  {visibleVehicleTypes.map((v) => (
                    <th key={v.id} className={stickyClassThFrozen}>
                      {displayTonnageClass(v.name)}
                    </th>
                  ))}
                  {trailerCols.map((t) => trailerTh(t, stickyClassThFrozen))}
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                <tr className="hover:bg-secondary/50 transition-colors group">
                  <td
                    className={stickyFieldTd}
                    title="Vehicles: from Assets (read-only). Trailers: entered here, as Assets cannot tell trailer types apart yet."
                  >
                    Quantity
                  </td>
                  {visibleVehicleTypes.map((v) => (
                    <td key={v.id} className={`${valueTdClass} text-muted-foreground`}>
                      {qtyDisplay(v.name)}
                    </td>
                  ))}
                  {trailerCols.map((t) =>
                    trailerQtyFromAssets(t) ? (
                      <td key={t.id} className={`${valueTdClass} text-gray-400`} title="From the Deck column on Assets (read-only)">
                        {t.quantity}
                      </td>
                    ) : (
                      trailerNumberCell(t, 'quantity', t.quantity, (n) => n, 1)
                    ),
                  )}
                </tr>
                {CAPITAL_ROW_GROUPS.map((group) => {
                  const usesMethod = (v: FleetCostVehicleType) =>
                    acquisitionMethodsInUse(v).includes(group.method);
                  const trailerUses = (t: TrailerTypeRow) => acquisitionMethodsInUse(t).includes(group.method);
                  return (
                  <Fragment key={group.method}>
                    <tr>
                      <td className={capitalGroupTd}>{ACQUISITION_METHOD_LABELS[group.method]}</td>
                      <td className="bg-background/40" colSpan={visibleVehicleTypes.length + trailerCols.length} />
                    </tr>
                    <tr className="hover:bg-secondary/50 transition-colors group">
                      <td
                        className={stickyFieldTd}
                        title="From the Acquisition column on Assets (read-only). Vehicles with no Acquisition set count as purchased."
                      >
                        Vehicles
                      </td>
                      {visibleVehicleTypes.map((v) => {
                        const mix = acquisitionMixCounts(v);
                        // Purchase shows only vehicles tagged Purchase; untagged
                        // ones (weighted as purchase) have their own row below.
                        const count = mix.entered
                          ? mix.counts[group.method] - (group.method === 'purchase' ? mix.unset : 0)
                          : 0;
                        return (
                          <td
                            key={v.id}
                            className={`${valueTdClass} ${count > 0 ? 'text-foreground' : 'text-muted-foreground'}`}
                            title="From Assets (read-only)"
                          >
                            {acquisitionMixByName ? count : qtyDisplay(v.name) === '…' ? '…' : UNKNOWN_CELL}
                          </td>
                        );
                      })}
                      {trailerCols.map((t) => {
                        const mix = acquisitionMixCounts(t);
                        // As for vehicles: trailers with no Acquisition have their own row below.
                        const count = mix.entered
                          ? mix.counts[group.method] - (group.method === 'purchase' ? mix.unset : 0)
                          : trailerUses(t) ? t.quantity : 0;
                        return (
                          <td key={t.id} className={`${valueTdClass} ${count > 0 ? 'text-foreground' : 'text-muted-foreground'}`}>
                            {count}
                          </td>
                        );
                      })}
                    </tr>
                    {group.rows.map((row) => (
                      <tr key={row.field} className="hover:bg-secondary/50 transition-colors group">
                        <td className={stickyFieldTd}>{row.label}</td>
                        {visibleVehicleTypes.map((v) => {
                          const raw = v[row.field];
                          const current = typeof raw === 'number' ? raw : row.defaultVal(v);
                          const defaultValue = resolveDefaultVehicleField(
                            v.name,
                            row.field,
                            row.defaultVal(fleetDefaultByName.get(v.name) ?? v),
                          );
                          return (
                            <Fragment key={v.id}>
                              {rateValueCell(current, defaultValue, {
                                confirmKey: fleetCostConfirmKeyVehicle(
                                  vehicleConfirmTable,
                                  v.name,
                                  row.field,
                                ),
                                formatDefault: row.display,
                                edit: editMode
                                  ? {
                                      step: row.step,
                                      onChange: (rawValue) => updateField(v.id, row.field, rawValue),
                                    }
                                  : undefined,
                                display: row.display(current),
                                unused: !usesMethod(v),
                              })}
                            </Fragment>
                          );
                        })}
                        {trailerCols.map((t) => {
                          const raw = t[row.field as keyof TrailerTypeRow];
                          return trailerNumberCell(
                            t,
                            row.field as keyof TrailerTypeRow,
                            typeof raw === 'number' ? raw : undefined,
                            row.display,
                            row.step,
                          );
                        })}
                      </tr>
                    ))}
                    {group.method === 'lease' ? (
                      <tr className="hover:bg-secondary/50 transition-colors group">
                        <td className={stickyFieldTd} title="Shown for reference: other maintenance costs are not changed">
                          Maintenance Included
                        </td>
                        {visibleVehicleTypes.map((v) => (
                          <td key={v.id} className={`${valueTdClass} ${usesMethod(v) ? 'text-foreground' : 'text-muted-foreground'}`}>
                            {editMode ? (
                              <input
                                type="checkbox"
                                checked={Boolean(v.leaseMaintenanceIncluded)}
                                onChange={(e) => updateVehicleRow(v.id, { leaseMaintenanceIncluded: e.target.checked })}
                                aria-label={`${displayTonnageClass(v.name)} lease includes maintenance`}
                              />
                            ) : v.leaseMaintenanceIncluded ? (
                              'Yes'
                            ) : (
                              'No'
                            )}
                          </td>
                        ))}
                        {trailerCols.map((t) => (
                          <td key={t.id} className={`${valueTdClass} ${trailerUses(t) ? 'text-foreground' : 'text-muted-foreground'}`}>
                            {editMode ? (
                              <input
                                type="checkbox"
                                checked={Boolean(t.leaseMaintenanceIncluded)}
                                onChange={(e) => updateTrailerType(t.id, { leaseMaintenanceIncluded: e.target.checked })}
                                aria-label={`${t.name} lease includes maintenance`}
                              />
                            ) : t.leaseMaintenanceIncluded ? (
                              'Yes'
                            ) : (
                              'No'
                            )}
                          </td>
                        ))}
                      </tr>
                    ) : null}
                    {group.method === 'rental' ? (
                      <tr className="hover:bg-secondary/50 transition-colors group">
                        <td className={stickyFieldTd}>Rental Period</td>
                        {visibleVehicleTypes.map((v) => (
                          <td key={v.id} className={`${valueTdClass} ${usesMethod(v) ? 'text-foreground' : 'text-muted-foreground'}`}>
                            {editMode ? (
                              <select
                                value={rentalPeriodOf(v)}
                                onChange={(e) =>
                                  updateVehicleRow(v.id, { rentalPeriod: e.target.value as FleetCostRentalPeriod })
                                }
                                className={capitalSelectClass}
                                aria-label={`${displayTonnageClass(v.name)} rental period`}
                              >
                                {(Object.keys(RENTAL_PERIOD_LABELS) as FleetCostRentalPeriod[]).map((p) => (
                                  <option key={p} value={p}>
                                    {RENTAL_PERIOD_LABELS[p]}
                                  </option>
                                ))}
                              </select>
                            ) : (
                              RENTAL_PERIOD_LABELS[rentalPeriodOf(v)]
                            )}
                          </td>
                        ))}
                        {trailerCols.map((t) => (
                          <td key={t.id} className={`${valueTdClass} ${trailerUses(t) ? 'text-foreground' : 'text-muted-foreground'}`}>
                            {editMode ? (
                              <select
                                value={rentalPeriodOf(t)}
                                onChange={(e) =>
                                  updateTrailerType(t.id, { rentalPeriod: e.target.value as FleetCostRentalPeriod })
                                }
                                className={capitalSelectClass}
                                aria-label={`${t.name} rental period`}
                              >
                                {(Object.keys(RENTAL_PERIOD_LABELS) as FleetCostRentalPeriod[]).map((p) => (
                                  <option key={p} value={p}>
                                    {RENTAL_PERIOD_LABELS[p]}
                                  </option>
                                ))}
                              </select>
                            ) : (
                              RENTAL_PERIOD_LABELS[rentalPeriodOf(t)]
                            )}
                          </td>
                        ))}
                      </tr>
                    ) : null}
                  </Fragment>
                  );
                })}
                <tr className="hover:bg-secondary/50 transition-colors group">
                  <td
                    className={stickyFieldTd}
                    title="Vehicles in this class with no Acquisition set on Assets. They are counted as purchased."
                  >
                    Acquisition Not Set
                  </td>
                  {visibleVehicleTypes.map((v) => {
                    const unset = acquisitionMixCounts(v).unset;
                    return (
                      <td
                        key={v.id}
                        className={`${valueTdClass} ${unset > 0 ? 'text-warning' : 'text-muted-foreground'}`}
                        title={unset > 0 ? 'Set Acquisition on the Assets page for these vehicles' : undefined}
                      >
                        {acquisitionMixByName || acquisitionMixCounts(v).entered ? unset : UNKNOWN_CELL}
                      </td>
                    );
                  })}
                  {trailerCols.map((t) => {
                    const unset = acquisitionMixCounts(t).unset;
                    return (
                      <td
                        key={t.id}
                        className={`${valueTdClass} ${unset > 0 ? 'text-warning' : 'text-muted-foreground'}`}
                        title={unset > 0 ? 'Set Acquisition on the Assets page for these trailers' : undefined}
                      >
                        {unset}
                      </td>
                    );
                  })}
                </tr>
                {trailerCols.length > 0 && deckCounts.unset > 0 ? (
                  <tr className="hover:bg-secondary/50 transition-colors group">
                    <td
                      className={stickyFieldTd}
                      title="Trailers with no Deck set on Assets. They are costed as single deck."
                    >
                      Deck Not Set
                    </td>
                    {visibleVehicleTypes.map((v) => notUsedTd(v.id, 'Deck applies to trailers only'))}
                    {trailerCols.map((t) =>
                      t.id === 'single-deck' && trailerTypesFromAssets ? (
                        <td
                          key={t.id}
                          className={`${valueTdClass} text-warning`}
                          title="Included in Single deck above. Set Deck on the Assets page for these trailers."
                        >
                          {deckCounts.unset}
                        </td>
                      ) : t.id === 'single-deck' ? (
                        <td
                          key={t.id}
                          className={`${valueTdClass} text-warning`}
                          title="No trailer has a Deck set on Assets yet. Set it there to count single and double deck."
                        >
                          {deckCounts.unset}
                        </td>
                      ) : (
                        notUsedTd(t.id, 'Unset trailers are counted under Single deck')
                      ),
                    )}
                  </tr>
                ) : null}
                <tr className="sticky bottom-0 z-40 hover:bg-secondary/50 transition-colors group bg-success-surface/90">
                  <td className={`${totalRowLabelTd} sticky left-0 z-50 bg-success-surface/90`}>
                    <span className="inline-flex items-center gap-0.5">
                      Total (£/yr/veh)
                      <CapitalTotalFormulaTip />
                    </span>
                  </td>
                  {visibleVehicleTypes.map((v) => (
                    <td key={v.id} className={`${totalRowValueTd} sticky bottom-0 z-40 bg-emerald-900/90`}>
                      {formatFleetMoneyK(vehicleCapitalAnnualisedGbp(v))}
                    </td>
                  ))}
                  {trailerCols.map((t) => (
                    <td
                      key={t.id}
                      className={`${totalRowValueTd} sticky bottom-0 z-40 bg-emerald-900/90`}
                      title={
                        !trailerSplit
                          ? 'Trailer capital not entered yet: the pool uses one standing rate'
                          : trailerCapitalMissing(t) ?? 'Per trailer'
                      }
                    >
                      {trailerSplit && !trailerCapitalMissing(t) ? formatFleetMoneyK(vehicleCapitalAnnualisedGbp(t)) : UNKNOWN_CELL}
                    </td>
                  ))}
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      )}

      {activeTab === 'operational' && (
        <div role="tabpanel">
          <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
            <p className="text-sm text-muted-foreground min-w-0 flex-1">
              Recurring standing costs per vehicle. Monthly items are annualised in the total.
              Trailer types have their own columns; their cost can be spread over the 44T fleet
              (Trailer Share below).
            </p>
            {editMode && trailerCols.length > 0 ? addTrailerTypeButton : null}
            {editActions}
          </div>

          {valueLegend}
          <div className="max-h-table-viewport overflow-x-auto overflow-y-auto rounded border border-border/60">
            <table className="w-full table-fixed text-sm border-separate border-spacing-0">
              <thead>
                <tr>
                  <th className={stickyFieldThFrozen}>Field</th>
                  {visibleVehicleTypes.map((v) => (
                    <th key={v.id} className={stickyClassThFrozen}>
                      {displayTonnageClass(v.name)}
                    </th>
                  ))}
                  {trailerCols.map((t) => trailerTh(t, stickyClassThFrozen))}
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                <tr className="hover:bg-secondary/50 transition-colors group">
                  <td className={stickyFieldTd} title="Vehicles: from Assets (read-only). Trailers: entered on Capital Costs.">
                    Quantity
                  </td>
                  {visibleVehicleTypes.map((v) => (
                    <td key={v.id} className={`${valueTdClass} text-muted-foreground`}>
                      {qtyDisplay(v.name)}
                    </td>
                  ))}
                  {trailerCols.map((t) => (
                    <td key={t.id} className={`${valueTdClass} text-gray-400`}>
                      {t.quantity}
                    </td>
                  ))}
                </tr>
                {trailerCols.length > 0 ? (
                  <tr className="hover:bg-gray-700/50 transition-colors group">
                    <td className={stickyFieldTd} title="Insurance, inspections and fixed maintenance per trailer">
                      Trailer Standing (£/yr)
                    </td>
                    {visibleVehicleTypes.map((v) => notUsedTd(v.id, 'Vehicles use the rows below'))}
                    {trailerCols.map((t) =>
                      trailerNumberCell(t, 'standing_gbp_per_trailer_year', t.standing_gbp_per_trailer_year, formatFleetMoneyK, 100),
                    )}
                  </tr>
                ) : null}
                {(
                  [
                    { label: 'Insurance (£/yr)', field: 'insurance' as const },
                    { label: 'Road Tax (£/yr)', field: 'roadTax' as const },
                    { label: 'O-License (£/yr)', field: 'oLicense' as const },
                    { label: 'Telematics (£/mo)', field: 'telematics' as const },
                    { label: 'Cleaning (£/mo)', field: 'cleaning' as const },
                    { label: 'Parking (£/mo)', field: 'parking' as const },
                    { label: 'Permits (£/yr)', field: 'permits' as const },
                    { label: 'Maintenance (£/yr)', field: 'scheduledMaintenance' as const },
                    { label: 'Compliance (£/yr)', field: 'vehicleCompliance' as const },
                    { label: 'Overhead (%)', field: 'overhead' as const },
                  ] as const
                ).map((row) => (
                  <tr key={row.field} className="hover:bg-secondary/50 transition-colors group">
                    <td className={stickyFieldTd}>{row.label}</td>
                    {visibleVehicleTypes.map((v) => {
                      const saved = v[row.field];
                      const numeric =
                        typeof saved === 'number' ? saved : FLEET_STANDING_OP_DEFAULTS[row.field];
                      const defaultValue = resolveDefaultVehicleField(
                        v.name,
                        row.field,
                        FLEET_STANDING_OP_DEFAULTS[row.field],
                      );
                      const display =
                        row.field === 'overhead' ? `${numeric}%` : formatFleetMoneyK(numeric);
                      return (
                        <Fragment key={v.id}>
                          {rateValueCell(numeric, defaultValue, {
                            confirmKey: fleetCostConfirmKeyVehicle(
                              vehicleConfirmTable,
                              v.name,
                              row.field,
                            ),
                            formatDefault: (n) =>
                              row.field === 'overhead' ? `${n}%` : formatFleetMoneyK(n),
                            edit: editMode
                              ? {
                                  step: row.field === 'overhead' ? '1' : '100',
                                  onChange: (rawValue) => updateField(v.id, row.field, rawValue),
                                }
                              : undefined,
                            display,
                          })}
                        </Fragment>
                      );
                    })}
                    {trailerCols.map((t) => notUsedTd(t.id, 'Trailers use Trailer Standing above'))}
                  </tr>
                ))}
                {trailerCols.length > 0 ? (
                  <>
                    <tr className="hover:bg-secondary/50 transition-colors group">
                      <td
                        className={stickyFieldTd}
                        title="Spread the trailer pool (every trailer type's capital and standing x quantity) evenly over the 44T vehicles. Used by Profitability Standing (£/day)."
                      >
                        Trailers Spread Over 44T
                      </td>
                      {visibleVehicleTypes.map((v) =>
                        v.name !== trailerStanding.allocate_to_tonnage_class ? (
                          notUsedTd(v.id, 'Trailers are spread over 44T only')
                        ) : (
                          <td key={v.id} className={`${valueTdClass} text-foreground`}>
                            {editMode ? (
                              <select
                                value={trailerStanding.allocation}
                                onChange={(e) =>
                                  setTrailerStandingField('allocation', e.target.value === 'none' ? 'none' : 'artic_fleet_average')
                                }
                                className={capitalSelectClass}
                                aria-label="Spread trailer costs over 44T"
                              >
                                <option value="artic_fleet_average">Yes</option>
                                <option value="none">No</option>
                              </select>
                            ) : trailerStanding.allocation === 'none' ? (
                              'No'
                            ) : (
                              'Yes'
                            )}
                          </td>
                        ),
                      )}
                      {trailerCols.map((t) => notUsedTd(t.id))}
                    </tr>
                    <tr className="hover:bg-secondary/50 transition-colors group">
                      <td
                        className={stickyFieldTd}
                        title={`Trailer pool ${formatFleetMoneyK(trailerStandingPoolAnnualGbp(trailerStanding))}/yr divided by the 44T quantity`}
                      >
                        Trailer Share (£/yr)
                      </td>
                      {visibleVehicleTypes.map((v) =>
                        v.name !== trailerStanding.allocate_to_tonnage_class ? (
                          notUsedTd(v.id, 'Trailers are spread over 44T only')
                        ) : trailerStanding.allocation === 'none' ? (
                          notUsedTd(v.id, 'Trailers are not spread over 44T')
                        ) : trailerStanding.allocate_to_vehicle_quantity <= 0 ? (
                          <td key={v.id} className={`${valueTdClass} text-warning`} title="No 44T vehicles in Assets: the trailer cost cannot be spread">
                            {UNKNOWN_CELL}
                          </td>
                        ) : (
                          <td key={v.id} className={`${valueTdClass} text-foreground`}>
                            {formatFleetMoneyK(trailerStandingAllocatedAnnualPerVehicleGbp(trailerStanding))}
                          </td>
                        ),
                      )}
                      {trailerCols.map((t) => notUsedTd(t.id))}
                    </tr>
                  </>
                ) : null}
                <tr className="sticky bottom-0 z-40 hover:bg-secondary/50 transition-colors group bg-success-surface/90">
                  <td className={`${totalRowLabelTd} sticky left-0 z-50 bg-success-surface/90`}>
                    Total (£/yr/veh)
                  </td>
                  {visibleVehicleTypes.map((v) => (
                    <td key={v.id} className={`${totalRowValueTd} sticky bottom-0 z-40 bg-success-surface/90`}>
                      {formatFleetMoneyK(fleetStandingAnnualGbp(v))}
                    </td>
                  ))}
                  {trailerCols.map((t) => (
                    <td key={t.id} className={`${totalRowValueTd} sticky bottom-0 z-40 bg-emerald-900/90`} title="Per trailer">
                      {formatFleetMoneyK(t.standing_gbp_per_trailer_year)}
                    </td>
                  ))}
                </tr>
              </tbody>
            </table>
          </div>
          {trailerCols.length > 0
            && trailerStanding.allocation !== 'none'
            && trailerStandingPoolAnnualGbp(trailerStanding) > 0
            && trailerStanding.allocate_to_vehicle_quantity <= 0 ? (
            <p className="text-xs text-amber-400/90 mt-3">
              No 44T vehicles in Assets: the trailer cost cannot be spread until the roster includes them.
            </p>
          ) : null}
        </div>
      )}

      {activeTab === 'running' && (
        <div role="tabpanel">
          {!activeProfit ? (
            <p className="text-sm text-warning/90 mb-4">
              Profitability assumptions are not available yet. Seed Costs config, or open Costs below and retry.
            </p>
          ) : (
            <>
              <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
                <p className="text-sm text-muted-foreground min-w-0 flex-1">
                  Fuel, tyres, variable repairs, and driver rates used by the Profitability report.
                </p>
                {editActions}
              </div>
              {valueLegend}
              <div className="max-h-table-viewport overflow-x-auto overflow-y-auto rounded border border-gray-700/60">
                <table className="w-full table-fixed text-sm border-separate border-spacing-0">
                  <thead>
                    <tr>
                      <th className={stickyFieldThFrozen}>Field</th>
                      {visibleRunningKeys.map((key) => (
                        <th key={key} className={stickyClassThFrozen}>
                          {displayTonnageClass(key)}
                        </th>
                      ))}
                      {trailerCols.map((t) => trailerTh(t, stickyClassThFrozen))}
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-border">
                    <tr className="hover:bg-secondary/50 transition-colors group">
                      <td className={stickyFieldTd} title="From Assets — read-only">
                        Quantity
                      </td>
                      {visibleRunningKeys.map((key) => (
                        <td key={key} className={`${valueTdClass} text-muted-foreground`}>
                          {qtyDisplay(key)}
                        </td>
                      ))}
                      {trailerCols.map((t) => (
                        <td key={t.id} className={`${valueTdClass} text-gray-400`}>
                          {t.quantity}
                        </td>
                      ))}
                    </tr>
                    {(
                      [
                        {
                          label: 'Fuel (MPG)',
                          map: 'fuel_mpg_tonnage',
                          get: (key: string) =>
                            (runningMpgMap as Record<string, number | undefined>)[key] ?? 0,
                          getDefault: (key: string) =>
                            (profitDefaults?.fuel_mpg_tonnage as Record<string, number | undefined> | undefined)?.[key],
                          set: (key: string, n: number) => setRunningMpg(key, n),
                        },
                        {
                          label: 'Fuel (£/mi)',
                          map: 'fuel_cost_tonnage',
                          get: (key: string) =>
                            (runningFuelMap as Record<string, number | undefined>)[key] ?? 0,
                          getDefault: (key: string) =>
                            (profitDefaults?.fuel_cost_tonnage as Record<string, number | undefined> | undefined)?.[key],
                          set: (key: string, n: number) => setRunningFuel(key, n),
                        },
                        {
                          label: 'Tyres (£/mi)',
                          map: 'tyre_cost_tonnage',
                          get: (key: string) =>
                            (runningTyreMap as Record<string, number | undefined>)[key] ?? 0,
                          getDefault: (key: string) =>
                            (profitDefaults?.tyre_cost_tonnage as Record<string, number | undefined> | undefined)?.[key],
                          set: (key: string, n: number) =>
                            setRunningMileageRate('tyre_cost_tonnage', key, n),
                        },
                        {
                          label: 'Repairs (£/mi)',
                          map: 'variable_repair_cost_tonnage',
                          get: (key: string) =>
                            (runningRepairMap as Record<string, number | undefined>)[key] ?? 0,
                          getDefault: (key: string) =>
                            (profitDefaults?.variable_repair_cost_tonnage as Record<string, number | undefined> | undefined)?.[key],
                          set: (key: string, n: number) =>
                            setRunningMileageRate('variable_repair_cost_tonnage', key, n),
                        },
                        {
                          label: 'Driver (£/mi)',
                          map: 'driver_cost_tonnage',
                          sub: 'mileage_rate' as const,
                          get: (key: string) =>
                            (runningDriverMap as Record<string, ProfitabilityDriverCostRate | undefined>)[
                              key
                            ]?.mileage_rate ?? 0,
                          getDefault: (key: string) =>
                            (profitDefaults?.driver_cost_tonnage as Record<string, ProfitabilityDriverCostRate | undefined> | undefined)?.[
                              key
                            ]?.mileage_rate,
                          set: (key: string, n: number) => setRunningDriver(key, 'mileage_rate', n),
                        },
                        {
                          label: 'Driving (£/hr)',
                          map: 'driver_cost_tonnage',
                          sub: 'driving_hourly_rate' as const,
                          get: (key: string) =>
                            (runningDriverMap as Record<string, ProfitabilityDriverCostRate | undefined>)[
                              key
                            ]?.driving_hourly_rate ?? 0,
                          getDefault: (key: string) =>
                            (profitDefaults?.driver_cost_tonnage as Record<string, ProfitabilityDriverCostRate | undefined> | undefined)?.[
                              key
                            ]?.driving_hourly_rate,
                          set: (key: string, n: number) =>
                            setRunningDriver(key, 'driving_hourly_rate', n),
                        },
                        {
                          label: 'Waiting (£/hr)',
                          map: 'driver_cost_tonnage',
                          sub: 'waiting_hourly_rate' as const,
                          get: (key: string) =>
                            (runningDriverMap as Record<string, ProfitabilityDriverCostRate | undefined>)[
                              key
                            ]?.waiting_hourly_rate ?? 0,
                          getDefault: (key: string) =>
                            (profitDefaults?.driver_cost_tonnage as Record<string, ProfitabilityDriverCostRate | undefined> | undefined)?.[
                              key
                            ]?.waiting_hourly_rate,
                          set: (key: string, n: number) =>
                            setRunningDriver(key, 'waiting_hourly_rate', n),
                        },
                      ] as const
                    ).map((row) => (
                      <tr key={row.label} className="hover:bg-secondary/50 transition-colors group">
                        <td className={stickyFieldTd}>
                          <span className="inline-flex items-center gap-1 min-w-0">
                            <span className="truncate">{row.label}</span>
                            {runningFieldInfo[row.label] ? (
                              <RunningFieldInfoTip label={`About ${row.label}`}>
                                {runningFieldInfo[row.label]}
                              </RunningFieldInfoTip>
                            ) : null}
                          </span>
                        </td>
                        {visibleRunningKeys.map((key) => {
                          const value = row.get(key);
                          const defaultValue = row.getDefault(key);
                          const confirmKey = fleetCostConfirmKeyRunning(
                            row.map,
                            key,
                            'sub' in row ? row.sub : undefined,
                          );
                          return (
                            <Fragment key={key}>
                              {rateValueCell(value, defaultValue, {
                                confirmKey,
                                formatDefault: (n) => String(n),
                                edit: editMode
                                  ? {
                                      step: '0.01',
                                      onChange: (rawValue) =>
                                        row.set(key, parseNonNegativeNumber(rawValue)),
                                    }
                                  : undefined,
                                display: value,
                              })}
                            </Fragment>
                          );
                        })}
                        {trailerCols.map((t) =>
                          notUsedTd(t.id, 'Running costs are not modelled per trailer: a trailer adds no fuel or driver cost of its own'),
                        )}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </>
          )}
        </div>
      )}

      </div>
      <div className="px-4 py-2 border-t border-border/80 bg-background/40 rounded-b-lg flex items-center justify-between gap-3">
        <div className="min-w-0 flex-1">
          {(metaUpdatedAt || metaUpdatedBy) ? (
            <ConfigMetaBadge
              variant="provenance"
              updatedAt={metaUpdatedAt}
              updatedBy={metaUpdatedBy}
            />
          ) : (
            <p className="text-xs text-muted-foreground">No saves recorded yet</p>
          )}
        </div>
        <ConfigChangeHistoryButton kind={isRunningTab ? 'profitability' : 'fleet-cost'} />
      </div>
    </div>
  );
}
